import { Box, Chip, Link, Stack, Typography, useTheme } from "@mui/material";
import { LaunchOutlined } from "@mui/icons-material";

import { SourceClassificationChip } from "./SourceClassificationChip";
import { safeHref } from "./safeHref";
import type {
  ExternalSourceType,
  ProvenanceExternalSource,
} from "../../types/provenance";

/**
 * Human-facing provider labels (contract §External sources). `source_type` is
 * the provider discriminator; unknown providers fall back to a generic label —
 * never a raw internal identifier.
 */
const PROVIDER_LABELS: Record<ExternalSourceType, string> = {
  nrc_adams: "NRC ADAMS",
  osti: "DOE OSTI",
  web: "Web",
  vnv: "VnV",
};

export const externalProviderLabel = (
  sourceType: ProvenanceExternalSource["source_type"],
): string => PROVIDER_LABELS[sourceType] ?? "External";

type ExternalSourceTierProps = {
  sources: ProvenanceExternalSource[];
};

/**
 * Renders external (ADAMS / OSTI / web / VnV) sources as compact link cards —
 * a subsection of the Searched tier, alongside the dataset collections that
 * were queried (#20423 close-gaps, schema 1.1.0). Each card shows the provider
 * label, title, family identifier (accession / OSTI id), a sanitized link
 * (backend admits http/https only; rendered as an anchor, never raw HTML),
 * published date, and which agent queried it.
 */
const ExternalSourceTier = ({ sources }: ExternalSourceTierProps) => {
  const theme = useTheme();

  if (!sources.length) return null;

  return (
    <Box data-testid="aa-src-external-list">
      {sources.map((source, index) => (
        <Box
          key={index}
          data-testid={`aa-src-external-item-${index}`}
          sx={{
            borderRadius: 1,
            m: 1,
            p: 1.5,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            sx={{ mb: 0.5 }}
          >
            <Chip
              data-testid={`aa-src-external-provider-${index}`}
              label={externalProviderLabel(source.source_type)}
              size="small"
              color="info"
              variant="outlined"
              sx={{ height: "22px", fontSize: "0.7rem" }}
            />
            <SourceClassificationChip classification={source.classification} />
          </Stack>

          {safeHref(source.url) ? (
            <Link
              href={safeHref(source.url)}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {source.title || source.identifier || source.url}
              <LaunchOutlined sx={{ fontSize: "0.85rem" }} />
            </Link>
          ) : (
            // No safe http(s) href — render as plain text, NOT a launch-styled
            // link that looks clickable but does nothing (#20423 L1 cosmetic).
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {source.title ||
                source.identifier ||
                source.url ||
                "Untitled source"}
            </Typography>
          )}

          {/* Family identifier (ADAMS accession / OSTI id) — shown when it
              isn't already the visible title text. */}
          {source.identifier && source.title && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={`aa-src-external-identifier-${index}`}
              sx={{ display: "block", mt: 0.25 }}
            >
              {source.identifier}
            </Typography>
          )}

          {source.snippet && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                mt: 0.5,
              }}
            >
              {source.snippet}
            </Typography>
          )}

          {(source.published_date || source.retrieved_by_agent) && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={`aa-src-external-meta-${index}`}
              sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}
            >
              {[
                source.published_date,
                source.retrieved_by_agent
                  ? `queried by ${source.retrieved_by_agent}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default ExternalSourceTier;
